/**
 * Gallery Section Component
 *
 * React implementation of the gallery section renderer.
 * Supports variants: grid, masonry, projects, before-after
 */

import { GalleryData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface GalleryProps extends SectionProps<GalleryData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Gallery({ data, ctx, sectionId, elements }: GalleryProps) {
  const variant = data.variant || 'grid';

  switch (variant) {
    case 'masonry':
      return <GalleryMasonry data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'projects':
      return <GalleryProjects data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'before-after':
      return <GalleryBeforeAfter data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <GalleryGrid data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// GRID VARIANT
// =============================================================================

function GalleryGrid({ data, ctx, elements }: GalleryProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="gallery"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.images?.map((image, i) => {
            const imageEl = findElement(elements, `images.${i}.url`);
            const captionEl = findElement(elements, `images.${i}.caption`);

            return (
              <div
                key={i}
                className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer"
                style={{ borderRadius: recipe.borders.imageRadius }}
              >
                {imageEl ? (
                  <EditableImage
                    element={imageEl}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                ) : (
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                  {(image.caption || captionEl) && (
                    <div className="p-4 text-white">
                      {captionEl ? (
                        <EditableText element={captionEl} as="p" className="text-sm" />
                      ) : (
                        <p className="text-sm">{image.caption}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// MASONRY VARIANT
// =============================================================================

function GalleryMasonry({ data, ctx, elements }: GalleryProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="gallery"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}
        </div>

        {/* Masonry Grid (CSS columns) */}
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {data.images?.map((image, i) => {
            const imageEl = findElement(elements, `images.${i}.url`);
            const captionEl = findElement(elements, `images.${i}.caption`);

            return (
              <div
                key={i}
                className="break-inside-avoid rounded-lg overflow-hidden"
                style={{ borderRadius: recipe.borders.imageRadius }}
              >
                {imageEl ? (
                  <EditableImage
                    element={imageEl}
                    alt={image.alt}
                    className="w-full"
                  />
                ) : (
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full"
                  />
                )}
                {(image.caption || captionEl) && (
                  <div
                    className="p-3 text-sm"
                    style={{
                      backgroundColor: recipe.colors.background,
                      color: recipe.colors.textMuted,
                    }}
                  >
                    {captionEl ? (
                      <EditableText element={captionEl} as="p" />
                    ) : (
                      <p>{image.caption}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// PROJECTS VARIANT
// =============================================================================

function GalleryProjects({ data, ctx, elements }: GalleryProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="gallery"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.projects?.map((project, i) => {
            const titleEl = findElement(elements, `projects.${i}.title`);
            const descEl = findElement(elements, `projects.${i}.description`);
            const imageEl = findElement(elements, `projects.${i}.image`);

            return (
              <div
                key={i}
                className="group overflow-hidden rounded-xl"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {/* Project Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  {imageEl ? (
                    <EditableImage
                      element={imageEl}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  )}
                  {project.category && (
                    <span
                      className="absolute top-4 left-4 px-3 py-1 text-xs font-semibold rounded-full text-white"
                      style={{ backgroundColor: recipe.colors.primary }}
                    >
                      {project.category}
                    </span>
                  )}
                </div>

                {/* Project Info */}
                <div className="p-6">
                  {titleEl ? (
                    <EditableText
                      element={titleEl}
                      as="h3"
                      className="text-xl font-semibold mb-2"
                      style={{ color: recipe.colors.text }}
                    />
                  ) : (
                    <h3
                      className="text-xl font-semibold mb-2"
                      style={{ color: recipe.colors.text }}
                    >
                      {project.title}
                    </h3>
                  )}

                  {(project.description || descEl) && (
                    descEl ? (
                      <EditableText
                        element={descEl}
                        as="p"
                        style={{ color: recipe.colors.textMuted }}
                      />
                    ) : (
                      <p style={{ color: recipe.colors.textMuted }}>
                        {project.description}
                      </p>
                    )
                  )}

                  {project.url && (
                    <a
                      href={project.url}
                      className="inline-flex items-center mt-4 font-medium transition-colors"
                      style={{ color: recipe.colors.primary }}
                    >
                      View Project
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// BEFORE-AFTER VARIANT
// =============================================================================

function GalleryBeforeAfter({ data, ctx, elements }: GalleryProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  // Filter projects that have before/after images
  const beforeAfterProjects = data.projects?.filter(p => p.beforeImage && p.afterImage) || [];

  return (
    <section
      id="gallery"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Before/After Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {beforeAfterProjects.map((project, i) => {
            const beforeImageEl = findElement(elements, `projects.${i}.beforeImage`);
            const afterImageEl = findElement(elements, `projects.${i}.afterImage`);
            const titleEl = findElement(elements, `projects.${i}.title`);
            const descEl = findElement(elements, `projects.${i}.description`);

            return (
              <div
                key={i}
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: recipe.colors.background,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {/* Before/After Images */}
                <div className="grid grid-cols-2">
                  <div className="relative">
                    {beforeImageEl ? (
                      <EditableImage
                        element={beforeImageEl}
                        alt={`${project.title} - Before`}
                        className="w-full aspect-[4/3] object-cover"
                      />
                    ) : (
                      <img
                        src={project.beforeImage}
                        alt={`${project.title} - Before`}
                        className="w-full aspect-[4/3] object-cover"
                      />
                    )}
                    <span className="absolute top-3 left-3 px-2 py-1 bg-black/60 text-white text-xs font-semibold rounded">
                      Before
                    </span>
                  </div>
                  <div className="relative">
                    {afterImageEl ? (
                      <EditableImage
                        element={afterImageEl}
                        alt={`${project.title} - After`}
                        className="w-full aspect-[4/3] object-cover"
                      />
                    ) : (
                      <img
                        src={project.afterImage}
                        alt={`${project.title} - After`}
                        className="w-full aspect-[4/3] object-cover"
                      />
                    )}
                    <span className="absolute top-3 left-3 px-2 py-1 bg-black/60 text-white text-xs font-semibold rounded">
                      After
                    </span>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-4">
                  {titleEl ? (
                    <EditableText
                      element={titleEl}
                      as="h3"
                      className="font-semibold"
                      style={{ color: recipe.colors.text }}
                    />
                  ) : (
                    <h3 className="font-semibold" style={{ color: recipe.colors.text }}>
                      {project.title}
                    </h3>
                  )}
                  {(project.description || descEl) && (
                    descEl ? (
                      <EditableText
                        element={descEl}
                        as="p"
                        className="text-sm mt-1"
                        style={{ color: recipe.colors.textMuted }}
                      />
                    ) : (
                      <p className="text-sm mt-1" style={{ color: recipe.colors.textMuted }}>
                        {project.description}
                      </p>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Gallery;
